import {OAUTH2_REDIRECT_URI, OAUTH_CODE_EXCHANGE_ENABLED} from "constants/index";

const STORAGE_KEY = "wirepackage.oauth.pkce";
// Per-tab volatile single-flight cache. Never put codes or returned tokens in storage.
const exchanges = new Map();
const failure = () => new Error("Google sign-in expired or failed. Please try again.");
const configurationFailure = message => Object.assign(new Error(message), {code: "GOOGLE_OAUTH_CONFIGURATION"});

function diagnosedFailure(stage, status) {
    if (process.env.NODE_ENV === "development") {
        // Fixed labels and HTTP status only: never log callback URLs or credentials.
        console.warn("Google sign-in diagnostic", {stage, ...(status ? {status} : {})});
    }
    return failure();
}

const base64url = bytes => btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

function apiUrl(path) {
    const url = new URL(process.env.REACT_APP_API_ENDPOINT + path);
    const development = process.env.NODE_ENV !== "production" && url.hostname === "localhost";
    if (url.protocol !== "https:" && !(development && url.protocol === "http:")) throw failure();
    return url;
}

export async function startGoogleOAuth(mode) {
    if (!OAUTH_CODE_EXCHANGE_ENABLED) throw configurationFailure("Google sign-in is not enabled on this site. Please contact support.");
    if (!["login", "signup"].includes(mode)) throw failure();
    if (!window.crypto || !window.crypto.subtle) throw configurationFailure("Google sign-in requires HTTPS or localhost. Please open the app using a secure address.");
    const callback = new URL(OAUTH2_REDIRECT_URI + "/" + mode);
    const productionOrigins = ["https://wirepackage.com", "https://www.wirepackage.com"];
    // Keep the callback on the initiating origin so its sessionStorage verifier is available.
    if (productionOrigins.includes(callback.origin) && productionOrigins.includes(window.location.origin)) {
        callback.host = new URL(window.location.origin).host;
    }
    if (callback.origin !== window.location.origin) throw configurationFailure("Google sign-in is configured for a different site address. Please contact support.");
    const redirectUri = callback.toString();
    const verifier = base64url(window.crypto.getRandomValues(new Uint8Array(32)));
    const challenge = base64url(await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({verifier, redirectUri, createdAt: Date.now()}));
    const url = apiUrl("/oauth2/authorize/google");
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");
    window.location.assign(url.toString());
}

// Must run BEFORE referral/storage operations. Preserve only the inspected referral parameter.
export function captureOAuthCallback(location, history) {
    const params = new URLSearchParams(location.search);
    const snapshot = {
        pathname: location.pathname, codes: params.getAll("code"),
        failed: params.has("error") || params.has("token"), ref: params.get("ref"), captured: true
    };
    const clean = new URLSearchParams();
    if (snapshot.ref !== null) clean.set("ref", snapshot.ref);
    const search = clean.toString() ? "?" + clean.toString() : "";
    if (history && history.replace) {
        try {
            history.replace({pathname: location.pathname, search, hash: "", state: undefined});
        } catch (_) {
            window.history.replaceState(null, "", location.pathname + search);
            throw failure();
        }
    } else {
        window.history.replaceState(null, "", location.pathname + search);
    }
    return snapshot;
}

export function finishGoogleFlow(pathname) {
    exchanges.delete(pathname);
}

export function exchangeGoogleCode(location, history) {
    const snapshot = location.captured ? location : captureOAuthCallback(location, history);
    const existing = exchanges.get(snapshot.pathname);
    if (existing && !snapshot.failed && (snapshot.codes.length === 0 ||
        (snapshot.codes.length === 1 && snapshot.codes[0] === existing.code))) return existing.promise;
    if (!OAUTH_CODE_EXCHANGE_ENABLED || snapshot.failed || snapshot.codes.length !== 1 ||
        !/^[A-Za-z0-9_-]{43}$/.test(snapshot.codes[0])) return Promise.reject(diagnosedFailure(
        !OAUTH_CODE_EXCHANGE_ENABLED ? "disabled" : snapshot.failed ? "backend-callback-rejected" : "missing-or-invalid-callback-code"));
    const record = {code: snapshot.codes[0]};
    record.promise = redeem(snapshot).then(token => {
        // Share only an in-flight request. Never retain a completed JWT as a module-level session.
        if (exchanges.get(snapshot.pathname) === record) exchanges.delete(snapshot.pathname);
        return token;
    }, () => {
        if (exchanges.get(snapshot.pathname) === record) exchanges.delete(snapshot.pathname);
        throw failure();
    });
    exchanges.set(snapshot.pathname, record);
    return record.promise;
}

async function redeem(snapshot) {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (!stored) throw diagnosedFailure("missing-browser-verifier");
    let pending;
    try {
        pending = JSON.parse(stored);
    } catch (_) {
        throw failure();
    }
    if (!pending || !/^[A-Za-z0-9_-]{43}$/.test(pending.verifier)
        || pending.redirectUri !== window.location.origin + snapshot.pathname
        || !Number.isFinite(pending.createdAt) || Date.now() - pending.createdAt > 240000
        || pending.createdAt > Date.now()) throw diagnosedFailure("expired-or-mismatched-browser-verifier");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
        const response = await fetch(apiUrl("/auth/oauth/exchange").toString(), {
            method: "POST", credentials: "omit", cache: "no-store", signal: controller.signal,
            headers: {"Content-Type": "application/json", "Accept": "application/json"},
            body: JSON.stringify({
                code: snapshot.codes[0],
                codeVerifier: pending.verifier,
                redirectUri: pending.redirectUri
            })
        }).catch(() => {
            throw diagnosedFailure("exchange-network-or-timeout");
        });
        if (!response.ok) throw diagnosedFailure("exchange-rejected", response.status);
        const body = await response.json();
        if (body.tokenType !== "Bearer" || typeof body.accessToken !== "string" || !body.accessToken) throw diagnosedFailure("invalid-exchange-response");
        return body.accessToken;
    } finally {
        clearTimeout(timeout);
    }
}

// Only pending requests are retained; completed JWTs are never cached here.
export function hasPendingGoogleExchange(pathname) {
    return exchanges.has(pathname);
}
